/**
 * Spec 054, step 3: «Переписка» — an order's chat with its client as people read it: both sides,
 * who wrote and when, whether it reached the client, the client's files. A person answers here in
 * their own name; inviting the client into Telegram and switching the chat off are here too.
 * Reading marks nothing for the manager; the tab asks the server again every 15 s while open.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Paperclip, Send } from "lucide-react";

import { CHAT_PAGE, ClientChat, type ChatFileOut, type ClientMessageOut, type InviteOut, type OrderOut } from "@/api/orders";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextArea } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { EmptyNote } from "@/kit";
import { cn } from "@/lib/cn";
import { CHAT_EVERY, chatLine, fileLine, mergeMessages, messageMeta } from "@/lib/clientChat";
import { clock } from "@/lib/lead";

export const chatKey = (orderId: string) => ["client-chat", orderId] as const;

/** A downloaded file handed to the browser under its own name. */
function saveFile(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function ClientChatTab({ order }: { order: OrderOut }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [older, setOlder] = useState<ClientMessageOut[]>([]);
  const [olderDone, setOlderDone] = useState(false);
  const [text, setText] = useState("");
  const [invite, setInvite] = useState<InviteOut | null>(null);
  const [revoking, setRevoking] = useState(false);
  const chat = useQuery({ queryKey: chatKey(order.id), queryFn: () => ClientChat.get(order.id), refetchInterval: CHAT_EVERY });
  const refresh = () => void qc.invalidateQueries({ queryKey: chatKey(order.id) });
  const messages = mergeMessages(older, chat.data?.messages ?? []);
  const earlier = useMutation({
    mutationFn: () => ClientChat.get(order.id, messages[0]?.created_at),
    onSuccess: (page) => {
      setOlder((was) => mergeMessages(page.messages, was));
      if (page.messages.length < CHAT_PAGE) setOlderDone(true);
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const answer = useMutation({
    mutationFn: () => ClientChat.answer(order.id, text.trim()),
    onSuccess: () => { setText(""); toast.success("Ушло клиенту"); refresh(); },
    onError: (error: Error) => toast.error(error.message),
  });
  const inviting = useMutation({
    mutationFn: () => ClientChat.invite(order.id),
    onSuccess: (link) => setInvite(link),
    onError: (error: Error) => toast.error(error.message),
  });
  const revoke = useMutation({
    mutationFn: () => ClientChat.revoke(order.id),
    onSuccess: () => { setRevoking(false); toast.success("Чат отключён"); refresh(); },
    onError: (error: Error) => toast.error(error.message),
  });
  const download = useMutation({
    mutationFn: async (file: ChatFileOut) => ({ file, blob: await ClientChat.file(file.id) }),
    onSuccess: ({ file, blob }) => saveFile(blob, file.name),
    onError: (error: Error) => toast.error(error.message),
  });
  if (chat.isLoading) return <SkeletonList count={3} />;
  if (!chat.data) {
    return <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void chat.refetch()}>Повторить</Button>}>Переписка не загрузилась.</EmptyNote>;
  }
  const c = chat.data;
  return (
    <>
      <Card className="flex flex-col gap-2">
        <p className="text-body text-p-text">{chatLine(c)}</p>
        <div className="flex flex-wrap gap-2">
          <Button size="compact" variant="secondary" loading={inviting.isPending} loadingText="Готовим ссылку…" onClick={() => inviting.mutate()}>
            {c.bound ? "Пригласить заново" : "Пригласить в Telegram"}
          </Button>
          {c.bound && <Button size="compact" variant="ghost" onClick={() => setRevoking(true)}>Отключить чат</Button>}
        </div>
      </Card>
      {!olderDone && c.messages.length >= CHAT_PAGE && (
        <Button size="compact" variant="ghost" loading={earlier.isPending} loadingText="Загружаем…" onClick={() => earlier.mutate()}>Раньше</Button>
      )}
      {messages.length === 0 ? (
        <EmptyNote>{c.bound ? "Клиент ещё не писал." : "Переписки нет. Пригласи клиента в Telegram: ссылка действует сутки."}</EmptyNote>
      ) : (
        <ol className="flex flex-col gap-2" aria-label="Переписка с клиентом">
          {messages.map((m) => <MessageRow key={m.id} message={m} onFile={(f) => download.mutate(f)} />)}
        </ol>
      )}
      {c.bound && (
        <Card className="flex flex-col gap-2">
          <TextArea label="Ответить клиенту" value={text} onChange={(e) => setText(e.target.value)} maxLength={4000}
            hint="Клиент увидит твоё имя и «Просто». Что он написал до этого, станет прочитанным — менеджер не ответит на это повторно. Пароли и ключи сюда не пиши." />
          <div className="flex justify-end">
            <Button icon={<Send className="h-4 w-4" aria-hidden="true" />} disabled={!text.trim()} loading={answer.isPending} loadingText="Отправляем…" onClick={() => answer.mutate()}>
              Отправить
            </Button>
          </div>
        </Card>
      )}
      <Sheet open={invite != null} onClose={() => setInvite(null)} title="Приглашение в Telegram">
        {invite && <InviteBody invite={invite} />}
      </Sheet>
      <Sheet open={revoking} onClose={() => setRevoking(false)} title="Отключить чат"
        footer={<Button block loading={revoke.isPending} loadingText="Отключаем…" onClick={() => revoke.mutate()}>Отключить</Button>}>
        <p className="text-body text-p-text">
          Бот перестанет передавать сюда слова клиента по этому заказу. Если клиент напишет снова, откроется новое обращение.
        </p>
      </Sheet>
    </>
  );
}

/** One message: its text, the client's files, a hidden secret, and who, when, what became of it. */
export function MessageRow({ message: m, onFile }: { message: ClientMessageOut; onFile?: (file: ChatFileOut) => void }) {
  const ours = m.direction === "out";
  return (
    <li className={cn("flex", ours ? "justify-end" : "justify-start")}>
      <div className={cn("flex max-w-[85%] flex-col gap-1 rounded-2xl border border-p-border px-3 py-2", ours ? "bg-p-surface-2" : "bg-p-surface")}>
        {m.body_md && <p className="whitespace-pre-wrap break-words text-body text-p-text">{m.body_md}</p>}
        {m.files.map((f) => (
          <button key={f.id} type="button" onClick={() => onFile?.(f)} className="flex min-h-9 items-center gap-1.5 text-left text-meta text-p-link">
            <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="break-all">{fileLine(f)}</span>
          </button>
        ))}
        {m.masked && <p className="text-meta text-p-text-muted">Пароль или ключ скрыт: в базе его нет.</p>}
        <p className={cn("text-meta", m.status === "failed" ? "font-semibold text-p-text" : "text-p-text-muted")}>{messageMeta(m)}</p>
      </div>
    </li>
  );
}

function InviteBody({ invite }: { invite: InviteOut }) {
  const toast = useToast();
  const copy = () => {
    navigator.clipboard.writeText(invite.url).then(
      () => toast.success("Ссылка скопирована"),
      () => toast.error("Не удалось скопировать: выдели ссылку и скопируй сам"),
    );
  };
  return (
    <div className="flex flex-col gap-3">
      <p className="select-all break-all rounded-xl border border-p-border px-3 py-2 text-body text-p-text">{invite.url}</p>
      <Button variant="secondary" onClick={copy}>Скопировать</Button>
      <p className="text-meta text-p-text-muted">
        Отправь ссылку клиенту сам. Он откроет её в Telegram — и чат привяжется к заказу. Ссылка одноразовая, действует до {clock(invite.expires_at)}.
      </p>
    </div>
  );
}
